import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

interface UserTenant {
  tenantId: string
  tenantPath: string
  tenantName: string
  permissions: Array<{ action: string, subject: string }>
}

interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  phone?: string
  tenants: UserTenant[]
}

// Track hydration state outside the store to avoid persistence issues
let _isHydrated = false
// Track if a refresh is in progress to prevent multiple simultaneous refreshes
let _refreshPromise: Promise<boolean> | null = null

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  const isHydrated = computed(() => _isHydrated)
  const currentPermissions = computed(() => {
    const tenantStore = useTenantStore()
    const currentTenant = user.value?.tenants.find(
      t => t.tenantId === tenantStore.currentTenantId
    )
    return currentTenant?.permissions || []
  })

  // Actions
  async function login(email: string, password: string) {
    isLoading.value = true
    error.value = null

    try {
      const config = useRuntimeConfig()
      const response = await $fetch<{
        user: User
        session: {
          accessToken: string
          refreshToken: string
        }
      }>(`${config.public.apiUrl}/api/v1/auth/login`, {
        method: 'POST',
        body: { email, password }
      })

      user.value = response.user
      accessToken.value = response.session.accessToken
      refreshToken.value = response.session.refreshToken

      // Set default tenant
      const tenantStore = useTenantStore()
      const firstTenant = response.user.tenants?.[0]
      if (firstTenant) {
        tenantStore.setCurrentTenant(firstTenant.tenantId)
      }

      return response
    } catch (err: any) {
      error.value = err.data?.message || 'Login failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function logout() {
    try {
      const config = useRuntimeConfig()
      if (accessToken.value) {
        await $fetch(`${config.public.apiUrl}/api/v1/auth/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken.value}`
          }
        }).catch(() => {
          // Ignore errors on logout
        })
      }
    } finally {
      clearAuth()
    }
  }

  function clearAuth() {
    user.value = null
    accessToken.value = null
    refreshToken.value = null
    _refreshPromise = null
  }

  async function refreshAccessToken(): Promise<boolean> {
    // If already refreshing, wait for that promise
    if (_refreshPromise) {
      return _refreshPromise
    }

    // If no refresh token, can't refresh
    if (!refreshToken.value) {
      return false
    }

    // Create a new refresh promise
    _refreshPromise = (async () => {
      try {
        const config = useRuntimeConfig()
        const response = await $fetch<{
          accessToken: string
          refreshToken: string
        }>(`${config.public.apiUrl}/api/v1/auth/refresh`, {
          method: 'POST',
          body: {
            refreshToken: refreshToken.value
          }
        })

        accessToken.value = response.accessToken
        refreshToken.value = response.refreshToken
        return true
      } catch {
        // Refresh failed, clear auth state
        clearAuth()
        return false
      } finally {
        _refreshPromise = null
      }
    })()

    return _refreshPromise
  }

  async function fetchProfile() {
    if (!accessToken.value) return

    try {
      const config = useRuntimeConfig()
      const tenantStore = useTenantStore()

      const response = await $fetch<User>(`${config.public.apiUrl}/api/v1/auth/me`, {
        headers: {
          'Authorization': `Bearer ${accessToken.value}`,
          'X-Tenant-Id': tenantStore.currentTenantId || ''
        }
      })

      user.value = response
    } catch (err: any) {
      // If 401, try to refresh token
      if (err.status === 401 || err.statusCode === 401) {
        const refreshed = await refreshAccessToken()
        if (refreshed) {
          // Retry fetch profile
          return fetchProfile()
        }
      }
      // If refresh failed or other error, logout
      clearAuth()
    }
  }

  function setTokens(newAccessToken: string, newRefreshToken: string) {
    accessToken.value = newAccessToken
    refreshToken.value = newRefreshToken
  }

  function markHydrated() {
    _isHydrated = true
  }

  return {
    // State
    user,
    accessToken,
    refreshToken,
    isLoading,
    error,
    // Getters
    isAuthenticated,
    isHydrated,
    currentPermissions,
    // Actions
    login,
    logout,
    clearAuth,
    refreshAccessToken,
    fetchProfile,
    setTokens,
    markHydrated
  }
}, {
  persist: {
    key: 'auth',
    paths: ['user', 'accessToken', 'refreshToken']
  }
})
